/**
 * KbSplash — фирменный loading screen, авто-определяет сабдомен и
 * рисует SVG-логотип, название раздела и подпись в стиле admin-hub
 * карточек (тот же градиент, та же иконка).
 *
 * Стили inline — компонент self-contained, работает в любом бандле
 * без зависимости от внешних CSS (раньше .kb-splash жил только в
 * chat-redesign.css → в sales/production/finance/warehouse был без
 * оформления). На мобильных размеры адаптируются через viewport units.
 */
import { resolveBrand, BRAND } from "@/lib/setBrandFavicon";


export function KbSplash({ label }: { label?: string }) {
  // BrixChat (brixchat.ru, 06.10.2026): своя заставка — знак brix из пакета бренда (не рисуем), «BrixChat»,
  // бегущая полоска; как стартовый экран в index.html
  if (typeof document !== "undefined" && document.documentElement.getAttribute("data-brand") === "brixchat") {
    return <BrixSplash label={label}/>;
  }
  const kind = resolveBrand("auto");
  const spec = BRAND[kind];
  const gradId = `kb_splash_${kind}`;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed", inset: 0,
        display: "grid", placeItems: "center",
        background: `linear-gradient(180deg, #ffffff 0%, ${hexToRgba(spec.from, 0.10)} 100%)`,
        zIndex: 99999,
        fontFamily:
          'Inter Tight, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: 24,
      }}
    >
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 18, padding: 32, maxWidth: 420, textAlign: "center",
      }}>
        <svg
          width={92} height={92}
          viewBox="0 0 64 64"
          aria-hidden="true"
          style={{
            display: "block",
            borderRadius: 22,
            boxShadow: `0 8px 28px ${hexToRgba(spec.from, 0.35)}, 0 2px 6px rgba(0,0,0,0.06)`,
            animation: "kb-splash-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={spec.from}/>
              <stop offset="100%" stopColor={spec.to}/>
            </linearGradient>
          </defs>
          <rect width="64" height="64" rx="14" fill={`url(#${gradId})`}/>
          <g fill="none" stroke="white" strokeWidth={4} strokeLinecap="round" strokeLinejoin="round">
            <path d={spec.iconPath}/>
          </g>
        </svg>

        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
          <div style={{
            fontSize: 11, fontWeight: 600, letterSpacing: 0.5,
            textTransform: "uppercase", color: "#9e9e9e",
          }}>
            Кирпичные беседки
          </div>
          <div style={{
            fontSize: 22, fontWeight: 700, color: "#1a1a1a",
            letterSpacing: -0.3, lineHeight: 1.2,
          }}>
            {spec.title}
          </div>
          <div style={{
            fontSize: 13, color: "#707579", lineHeight: 1.45,
            maxWidth: 320,
          }}>
            {spec.subtitle}
          </div>
        </div>

        <div style={{
          marginTop: 6,
          width: 32, height: 32,
          display: "grid", placeItems: "center",
        }} aria-hidden="true">
          <div style={{
            width: 28, height: 28,
            borderRadius: "50%",
            border: "2.5px solid rgba(0,0,0,0.08)",
            borderTopColor: spec.from,
            animation: "kb-splash-spin 0.8s linear infinite",
          }}/>
        </div>

        {label && (
          <div style={{
            marginTop: 2, fontSize: 13, color: "rgba(0,0,0,0.5)",
          }}>{label}</div>
        )}

        <style>{`
          @keyframes kb-splash-pop {
            0%   { transform: scale(0.7);  opacity: 0; }
            60%  { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1);    opacity: 1; }
          }
          @keyframes kb-splash-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}


/** Заставка BrixChat. Полоска бежит свойством left и с !important: внутри мессенджера transform и animation
 *  запрещены (chat-redesign.css: .chat-redesign * { …: none !important }). */
function BrixSplash({ label }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="bxs">
      <div className="bxs-box">
        <img src="/brand/brixchat/mark-256.png" width={72} height={72} alt=""/>
        <div className="bxs-name">BrixChat</div>
        <div className="bxs-bar" aria-hidden="true"><i/></div>
        {label && <div className="bxs-label">{label}</div>}
      </div>
      <style>{BRIX_SPLASH_CSS}</style>
    </div>
  );
}

const BRIX_SPLASH_CSS = `
.bxs{position:fixed;inset:0;display:grid;place-items:center;z-index:99999;padding:24px;background:radial-gradient(60vw 55vh at 0% 0%,rgba(0,95,255,.07),transparent 70%),radial-gradient(60vw 60vh at 100% 100%,rgba(123,77,255,.08),transparent 70%),linear-gradient(180deg,#f4f6fb 0%,#eef1f8 100%);font-family:Onest,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif}
.bxs .bxs-box{display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center}
.bxs .bxs-box img{display:block;width:72px;height:72px}
.bxs .bxs-name{font-size:24px;line-height:32px;font-weight:700;color:#10233f;letter-spacing:-.01em}
.bxs .bxs-bar{position:relative;width:160px;height:4px;border-radius:2px;background:rgba(0,95,255,.12);overflow:hidden}
.bxs .bxs-bar i{position:absolute;top:0;bottom:0;left:-40%;width:40%;border-radius:2px;background:linear-gradient(90deg,#005fff,#7b4dff);animation:bxs-run 1.1s ease-in-out infinite !important}
.bxs .bxs-label{font-size:14px;color:#56606d}
@keyframes bxs-run{0%{left:-40%}100%{left:100%}}
`;


function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const bigint = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  const r = (bigint >> 16) & 255, g = (bigint >> 8) & 255, b = bigint & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}
